import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { 
  Search, Filter, Grid, List, Eye, Check, ChevronRight,
  MessageSquare, Image, LayoutGrid, Smartphone, Tag, X,
  ShoppingCart, Car, Shield, CreditCard, Gem, Sparkles, Shirt, Wrench,
  Building, Heart, Package, Bell, Megaphone, Plane, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Industry icons mapping
const industryIcons = {
  retail: ShoppingCart,
  supermarket: ShoppingCart,
  ecommerce: ShoppingCart,
  automotive: Car,
  auto_parts: Wrench,
  insurance: Shield,
  finance: CreditCard,
  jewelry: Gem,
  cosmetics: Sparkles,
  fashion: Shirt,
  healthcare: Heart,
  travel: Plane,
  food: Building,
  general: MessageSquare
};

// Industry labels in French
const industryLabels = {
  retail: 'Grande Distribution',
  supermarket: 'Supermarchés',
  ecommerce: 'E-commerce',
  automotive: 'Automobile',
  auto_parts: 'Pièces Auto',
  insurance: 'Assurance',
  finance: 'Finance / Crédit',
  jewelry: 'Bijouterie',
  cosmetics: 'Parfumerie / Beauté',
  fashion: 'Mode',
  healthcare: 'Santé',
  travel: 'Voyage',
  food: 'Restauration',
  general: 'Général'
};

// Category labels in French
const categoryLabels = {
  marketing: 'Marketing',
  notification: 'Notification',
  promotion: 'Promotion',
  security: 'Sécurité',
  loyalty: 'Fidélisation',
  delivery: 'Livraison',
  appointment: 'Rendez-vous',
  payment: 'Paiement',
  customer_service: 'Service Client',
  feedback: 'Feedback'
};

// RCS Type labels and colors
const rcsTypeLabels = {
  rcs_basic: 'RCS Basic',
  rcs_rich: 'RCS Rich',
  rcs_chat: 'RCS Chat'
};

const rcsTypeColors = {
  rcs_basic: 'bg-gray-100 text-gray-700',
  rcs_rich: 'bg-blue-100 text-blue-700',
  rcs_chat: 'bg-purple-100 text-purple-700'
};

export default function TemplateSelector({ 
  open, 
  onOpenChange, 
  onSelectTemplate 
}) {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedRcsType, setSelectedRcsType] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [rcsTypes, setRcsTypes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [step, setStep] = useState('browse'); // 'browse' | 'preview' | 'customize'
  const [variableValues, setVariableValues] = useState({});
  const [allTemplates, setAllTemplates] = useState([]); // Store all templates unfiltered
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  const fetchFavorites = useCallback(async () => {
    if (!open) return;
    try {
      const response = await fetch(`${API_URL}/api/v2/templates/library/user/favorite-ids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavoriteIds(new Set(data.favorite_ids || []));
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setFavoritesLoaded(true);
    }
  }, [token, open]);

  const fetchTemplates = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/v2/templates/library?limit=100`;
      if (selectedCategory !== 'all') url += `&category=${selectedCategory}`;
      if (selectedIndustry !== 'all') url += `&industry=${selectedIndustry}`;
      if (selectedRcsType !== 'all') url += `&rcs_type=${selectedRcsType}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const templatesData = data.templates || [];
        setAllTemplates(templatesData);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory, selectedIndustry, selectedRcsType, search, open]);

  // Apply favorites filter client-side after both templates and favorites are loaded
  useEffect(() => {
    // Wait until both templates and favorites are loaded
    if (!favoritesLoaded || loading) {
      return;
    }
    
    if (showFavoritesOnly) {
      const filteredFavorites = allTemplates.filter(t => favoriteIds.has(t.id));
      console.log('Filtering favorites:', { 
        totalTemplates: allTemplates.length, 
        favoriteIds: Array.from(favoriteIds),
        filteredCount: filteredFavorites.length 
      });
      setTemplates(filteredFavorites);
    } else {
      setTemplates(allTemplates);
    }
  }, [showFavoritesOnly, allTemplates, favoriteIds, favoritesLoaded, loading]);

  const fetchFilters = useCallback(async () => {
    if (!open) return;
    try {
      const [catRes, indRes, rcsRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/templates/library/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/v2/templates/library/industries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/v2/templates/library/rcs-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
      }
      if (indRes.ok) {
        const indData = await indRes.json();
        setIndustries(indData);
      }
      if (rcsRes.ok) {
        const rcsData = await rcsRes.json();
        setRcsTypes(rcsData);
      }
    } catch (err) {
      console.error('Error fetching filters:', err);
    }
  }, [token, open]);

  useEffect(() => {
    if (open) {
      fetchFavorites();
      fetchFilters();
    }
  }, [open, fetchFavorites, fetchFilters]);

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [fetchTemplates, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('browse');
      setSelectedTemplate(null);
      setVariableValues({});
      setShowFavoritesOnly(false);
      setFavoritesLoaded(false);
    }
  }, [open]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    // Initialize variable values with empty strings
    const initialVars = {};
    (template.variables || []).forEach(v => {
      initialVars[v] = '';
    });
    setVariableValues(initialVars);
    setStep('preview');
  };

  const handleConfirmTemplate = async () => {
    if (!selectedTemplate) return;

    // Track usage
    try {
      await fetch(`${API_URL}/api/v2/templates/library/${selectedTemplate.id}/use`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      // Non-blocking
    }

    // Replace variables in content
    let content = JSON.parse(JSON.stringify(selectedTemplate.content));
    
    const replaceVariables = (obj) => {
      if (typeof obj === 'string') {
        let result = obj;
        Object.entries(variableValues).forEach(([key, value]) => {
          const placeholder = `{${key}}`;
          result = result.split(placeholder).join(value || placeholder);
        });
        return result;
      }
      if (Array.isArray(obj)) {
        return obj.map(replaceVariables);
      }
      if (typeof obj === 'object' && obj !== null) {
        const newObj = {};
        for (const key in obj) {
          newObj[key] = replaceVariables(obj[key]);
        }
        return newObj;
      }
      return obj;
    };

    content = replaceVariables(content);

    onSelectTemplate({
      template: selectedTemplate,
      content: content,
      message_type: content.message_type,
      traffic_type: 'PROMOTION'
    });

    toast.success('Template appliqué');
    onOpenChange(false);
  };

  const getMessageTypeIcon = (type) => {
    const icons = {
      text: MessageSquare,
      media: Image,
      rich_card: LayoutGrid,
      carousel: Grid
    };
    const Icon = icons[type] || MessageSquare;
    return <Icon className="w-4 h-4" />;
  };

  const renderPreviewContent = (content) => {
    if (!content) return null;

    // Apply variable replacements for preview
    const replaceVarsInString = (str) => {
      if (typeof str !== 'string') return str;
      let result = str;
      Object.entries(variableValues).forEach(([key, value]) => {
        const placeholder = `{${key}}`;
        if (value) {
          result = result.split(placeholder).join(value);
        }
      });
      return result;
    };

    return (
      <div className="w-64 bg-gray-100 rounded-3xl p-2 shadow-xl mx-auto">
        <div className="bg-white h-[420px] rounded-2xl overflow-hidden flex flex-col">
          {/* Status bar */}
          <div className="h-5 bg-gray-900 flex items-center justify-center">
            <div className="w-16 h-1 bg-gray-700 rounded-full" />
          </div>
          
          {/* Chat header */}
          <div className="px-3 py-2 border-b flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <Building className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <p className="font-medium text-xs">Votre Marque</p>
              <p className="text-[10px] text-gray-500">Message RCS</p>
            </div>
          </div>
          
          {/* Message content */}
          <ScrollArea className="flex-1 p-3">
            {content.message_type === 'text' && (
              <div className="bg-gray-100 rounded-lg p-2.5 max-w-[90%]">
                <p className="text-xs whitespace-pre-wrap">{replaceVarsInString(content.text)}</p>
              </div>
            )}
            
            {content.message_type === 'rich_card' && (
              <div className="bg-white border rounded-lg overflow-hidden max-w-[90%] shadow-sm">
                {content.media_url && (
                  <div className="h-24 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                    <Image className="w-8 h-8 text-white/50" />
                  </div>
                )}
                <div className="p-2">
                  {content.title && (
                    <p className="font-semibold text-xs mb-1">{replaceVarsInString(content.title)}</p>
                  )}
                  {content.description && (
                    <p className="text-[10px] text-gray-600 whitespace-pre-wrap">{replaceVarsInString(content.description)}</p>
                  )}
                </div>
                {content.suggestions?.length > 0 && (
                  <div className="px-2 pb-2 flex flex-wrap gap-1">
                    {content.suggestions.slice(0, 3).map((s, i) => (
                      <button key={i} className="text-[10px] px-2 py-1 rounded-full border border-indigo-500 text-indigo-600">
                        {replaceVarsInString(s.text)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {content.message_type === 'carousel' && content.carousel_cards && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {content.carousel_cards.slice(0, 3).map((card, i) => (
                  <div key={i} className="flex-shrink-0 w-36 bg-white border rounded-lg overflow-hidden shadow-sm">
                    <div className="h-16 bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                      <Image className="w-6 h-6 text-white/50" />
                    </div>
                    <div className="p-1.5">
                      <p className="font-semibold text-[10px] mb-0.5 truncate">{replaceVarsInString(card.title)}</p>
                      <p className="text-[10px] text-gray-600 line-clamp-2">{replaceVarsInString(card.description)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          {/* Input bar */}
          <div className="p-2 border-t">
            <div className="bg-gray-100 rounded-full px-3 py-1.5 text-[10px] text-gray-400">
              Écrire un message...
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="template-selector-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-indigo-600" />
            {step === 'browse' && 'Bibliothèque de Templates'}
            {step === 'preview' && (
              <span className="flex items-center gap-2">
                <button onClick={() => setStep('browse')} className="text-gray-400 hover:text-gray-600">
                  Templates
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                {selectedTemplate?.name}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === 'browse' && 'Choisissez un template pour votre campagne RCS'}
            {step === 'preview' && 'Personnalisez les variables et prévisualisez le message'}
          </DialogDescription>
        </DialogHeader>

        {/* Browse Step */}
        {step === 'browse' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 pb-4 border-b">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher un template..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="template-search"
                />
              </div>
              
              {/* Favorites Filter */}
              <Button
                variant={showFavoritesOnly ? 'default' : 'outline'}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`gap-2 ${showFavoritesOnly ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
                data-testid="filter-favorites-selector"
              >
                <Star className={`w-4 h-4 ${showFavoritesOnly ? 'fill-white' : ''}`} />
                Favoris ({favoriteIds.size})
              </Button>
              
              <Select value={selectedRcsType} onValueChange={setSelectedRcsType}>
                <SelectTrigger className="w-40">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type RCS" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous types</SelectItem>
                  {rcsTypes.map(type => (
                    <SelectItem key={type.rcs_type} value={type.rcs_type}>
                      {rcsTypeLabels[type.rcs_type] || type.rcs_type} ({type.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-44">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category} value={cat.category}>
                      {categoryLabels[cat.category] || cat.category} ({cat.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger className="w-44">
                  <Building className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Secteur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous secteurs</SelectItem>
                  {industries.map(ind => (
                    <SelectItem key={ind.industry} value={ind.industry}>
                      {industryLabels[ind.industry] || ind.industry} ({ind.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quick Industry Filters */}
            <div className="flex flex-wrap gap-2 py-3">
              {Object.entries(industryLabels).slice(0, 6).map(([key, label]) => {
                const Icon = industryIcons[key] || Building;
                const isActive = selectedIndustry === key;
                return (
                  <Button
                    key={key}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedIndustry(isActive ? 'all' : key)}
                    className={`gap-1.5 ${isActive ? 'bg-indigo-600' : ''}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </Button>
                );
              })}
            </div>

            {/* Templates Grid */}
            <ScrollArea className="flex-1 min-h-[300px]">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
                  <p className="mt-4 text-gray-500">Chargement...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun template trouvé</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pr-4">
                  {templates.map(template => {
                    const IndustryIcon = industryIcons[template.industry] || Building;
                    const isFavorite = favoriteIds.has(template.id);
                    
                    return (
                      <div
                        key={template.id || template.name}
                        className={`border rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer transition-all group ${isFavorite ? 'ring-2 ring-yellow-300 border-yellow-300' : ''}`}
                        onClick={() => handleSelectTemplate(template)}
                        data-testid={`template-item-${template.name}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getMessageTypeIcon(template.content?.message_type)}
                            {/* RCS Type Badge */}
                            {template.rcs_type && (
                              <Badge className={`text-[10px] ${rcsTypeColors[template.rcs_type] || 'bg-gray-100 text-gray-700'}`}>
                                {rcsTypeLabels[template.rcs_type]}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {isFavorite && (
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            )}
                            {template.is_system && (
                              <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">
                                Système
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <h3 className="font-medium text-sm mb-1 group-hover:text-indigo-700">
                          {template.name}
                        </h3>
                        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                          {template.description}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="gap-1 text-[10px]">
                            <IndustryIcon className="w-3 h-3" />
                            {industryLabels[template.industry] || template.industry}
                          </Badge>
                          
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </>
        )}

        {/* Preview Step */}
        {step === 'preview' && selectedTemplate && (
          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
              {/* Left: Details & Variables */}
              <div className="space-y-4 overflow-y-auto pr-2">
                <div className="flex items-center gap-2 mb-2">
                  {getMessageTypeIcon(selectedTemplate.content?.message_type)}
                  <Badge variant="outline">
                    {selectedTemplate.content?.message_type}
                  </Badge>
                  <Badge variant="secondary">
                    {industryLabels[selectedTemplate.industry]}
                  </Badge>
                  <Badge variant="secondary">
                    {categoryLabels[selectedTemplate.category]}
                  </Badge>
                </div>
                
                <p className="text-sm text-gray-600">{selectedTemplate.description}</p>
                
                {/* Variables Editor */}
                {selectedTemplate.variables?.length > 0 && (
                  <div className="bg-indigo-50 rounded-lg p-4 space-y-3">
                    <Label className="font-semibold text-indigo-900 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Variables à personnaliser
                    </Label>
                    <p className="text-xs text-indigo-700 mb-3">
                      Remplissez les champs ci-dessous pour personnaliser votre message
                    </p>
                    
                    <div className="space-y-3">
                      {selectedTemplate.variables.map(variable => (
                        <div key={variable} className="space-y-1">
                          <Label className="text-xs font-medium text-gray-700">
                            {`{${variable}}`}
                          </Label>
                          <Input
                            placeholder={`Valeur pour ${variable}...`}
                            value={variableValues[variable] || ''}
                            onChange={(e) => setVariableValues(prev => ({
                              ...prev,
                              [variable]: e.target.value
                            }))}
                            className="bg-white"
                            data-testid={`variable-${variable}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Template content preview (text) */}
                {selectedTemplate.content?.message_type === 'text' && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Label className="text-xs text-gray-500 mb-2 block">Contenu du message</Label>
                    <p className="text-sm whitespace-pre-wrap">
                      {selectedTemplate.content.text}
                    </p>
                  </div>
                )}

                {/* Suggestions preview */}
                {selectedTemplate.content?.suggestions?.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500 mb-2 block">Boutons de suggestion</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.content.suggestions.map((s, i) => (
                        <Badge key={i} variant="outline" className="gap-1">
                          {s.type === 'url' && <Smartphone className="w-3 h-3" />}
                          {s.text}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right: Mobile Preview */}
              <div className="flex flex-col items-center justify-center bg-gray-50 rounded-lg p-4">
                <Label className="text-xs text-gray-500 mb-4">Aperçu mobile</Label>
                {renderPreviewContent(selectedTemplate.content)}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 pt-4 border-t">
          {step === 'browse' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
          )}
          
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('browse')}>
                Retour
              </Button>
              <Button 
                onClick={handleConfirmTemplate}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-template-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Utiliser ce template
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
